import { format, isValid, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { NumericFormat } from 'react-number-format';
import styled from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { SOURCE } from '_constants/transactions';
import { TRANSACTION_TYPES } from '_constants/transactions';
import { hexToRGBA, luminance } from '_helpers/theme';

import Icon from 'components/atomic/Icon';
import Text from 'components/atomic/layouts/Text';

const StyledContainer = styled.div`
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  border-radius: 8px;
`;

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spaces.regular}px;
  padding: ${theme.spaces.regular}px;
`;

const StyledRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${theme.spaces.smallX}px;
`;

const StyledCol = styled.div``;

const StyledActionContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const TransactionsListItem = ({
  source,
  type,
  date,
  accountName,
  contactName,
  currencyCode,
  total,
  reference,
  onUpdate,
  onDelete,
}) => {
  const parsedDate = parseISO(date);

  return (
    <StyledContainer>
      <StyledBody>
        <StyledRow>
          <StyledCol>
            <Icon
              alias={type === TRANSACTION_TYPES.spend ? ICON_ALIASES.cardSend : ICON_ALIASES.cardReceive}
              color={
                type === TRANSACTION_TYPES.spend
                  ? luminance(theme.colors.red.primary, 0.4)
                  : luminance(theme.colors.green.primary, 0.1)
              }
            />
          </StyledCol>
          <StyledCol>
            <Text
              size={theme.fontSizes.regular}
              weight={700}
              color={
                type === TRANSACTION_TYPES.spend
                  ? luminance(theme.colors.red.primary, 0.4)
                  : luminance(theme.colors.green.primary, 0.1)
              }
              bottomSpace={theme.spaces.smallX}
            >
              {type} ({reference})
            </Text>
            <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary}>
              Date: {isValid(parsedDate) ? format(parsedDate, 'MM/dd/yyyy') : date}
            </Text>
            <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary}>
              Account Name: {accountName || 'N/A'}
            </Text>
            <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} bottomSpace={theme.spaces.smallX}>
              Contact Name: {contactName || 'N/A'}
            </Text>
            <Text
              size={theme.fontSizes.regular}
              weight={600}
              color={theme.colors.gray.primary}
              ignoreSpan
              bottomSpace={theme.spaces.regular}
            >
              Total:&nbsp;
              <NumericFormat
                displayType="text"
                prefix="$"
                value={total}
                thousandSeparator=","
                decimalScale={2}
                fixedDecimalScale
              />
              &nbsp;{currencyCode}
            </Text>
            {[SOURCE.customer, SOURCE.plaid].includes(source) && (
              <Row>
                <Col xs={6}>
                  <StyledActionContainer onClick={onUpdate}>
                    <Icon
                      alias={ICON_ALIASES.documentEdit}
                      height={16}
                      width={16}
                      color={theme.colors.indigo.primary}
                    />
                    <Text size={theme.fontSizes.small} color={theme.colors.indigo.primary}>
                      Update
                    </Text>
                  </StyledActionContainer>
                </Col>
                <Col xs={6}>
                  <StyledActionContainer onClick={onDelete}>
                    <Icon alias={ICON_ALIASES.trash} height={16} width={16} color={theme.colors.gray.secondary} />
                    <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary}>
                      Delete
                    </Text>
                  </StyledActionContainer>
                </Col>
              </Row>
            )}
          </StyledCol>
        </StyledRow>
      </StyledBody>
    </StyledContainer>
  );
};

TransactionsListItem.propTypes = {
  source: PropTypes.string,
  type: PropTypes.string,
  date: PropTypes.string,
  accountName: PropTypes.string,
  contactName: PropTypes.string,
  currencyCode: PropTypes.string,
  total: PropTypes.number,
  reference: PropTypes.string,
  onUpdate: PropTypes.func,
  onDelete: PropTypes.func,
};

export default TransactionsListItem;
